import loadable from '@loadable/component';

import { Box, Grid } from '@simplywallst/ui-core';

import { useUserAccess } from '@/hooks/useUserAccess';
import { pluralise } from '@/utilities/pluralise';
import { useFlag } from '@/features/unleash';

import PortfolioLinkStatus from '../PortfolioLinkStatus';
import { HoldingsOverview } from '../HoldingsOverview';

import { useHoldingsLimits } from '@/hooks/portfolio/useHoldingsLimits';
import { useSortedHoldings } from '../../hooks/useHoldingsSorting';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import { usePortfolioType } from '../HoldingsTable/hooks';

const PortfolioOverview = loadable(() => import('../PortfolioOverview'), {
  resolveComponent: (c) => c.PortfolioOverview,
  fallback: (
    <Grid
      templateRows="400px 400px"
      templateRowsMd="405px"
      templateColumnsMd="auto 32%"
      gap="16px"
    >
      <Grid backgroundColor="noir" borderRadius="x1" />
      <Grid backgroundColor="noir" borderRadius="x1" />
    </Grid>
  ),
});

const PortfolioLimitsBanner = loadable(
  () => import('../PortfolioLimitsBanner'),
  {
    resolveComponent: (c) => c.PortfolioLimitsBanner,
  }
);

const AccurateReturnsBanner = loadable(
  () => import('./AccurateReturnsBanner'),
  {
    resolveComponent: (c) => c.AccurateReturnsBanner,
  }
);

interface Props {
  portfolio: Portfolio;
}

export const HoldingsPanel = ({ portfolio }: Props) => {
  const flag = useFlag({
    name: 'mono-portfolio-transactions-table',
  });

  const canAccessTransactions = flag.flagsReady && flag.enabled;

  const { data: userAccess } = useUserAccess();

  const { holdings, excludedHoldings } = useHoldingsLimits({
    portfolioHoldings: portfolio.holdings,
  });

  const sortedHoldings = useSortedHoldings(holdings);

  const portfolioType = usePortfolioType(portfolio);

  return (
    <Grid templateColumns="100vw" templateColumnsLg="auto">
      {portfolioType === 'transaction-portfolio' && canAccessTransactions && (
        <AccurateReturnsBanner />
      )}
      {portfolio.isAnalysisRestricted ? (
        <Box marginTop="x2" marginTopMd="x0" marginBottom="x2" minHeight="56px">
          <PortfolioLimitsBanner
            data-cy-id="portfolio-limits-banner-holdings-panel"
            content={`You've reached the portfolio limit on your ${userAccess.planType} plan. To view this portfolio, consider upgrading your plan or removing excess portfolios.`}
          />
        </Box>
      ) : excludedHoldings.length > 0 ? (
        <Box marginTop="x2" marginTopMd="x0" marginBottom="x2">
          <PortfolioLimitsBanner
            data-cy-id="excluded-holdings-banner-holdings-panel"
            content={`${excludedHoldings.length} ${pluralise(
              excludedHoldings.length,
              'holding',
              'holdings'
            )} excluded from analysis due to ${
              userAccess.planType
            } plan limit being reached. Upgrade for full access.`}
          />
        </Box>
      ) : null}
      <section data-cy-id="holdings-panel-portfolio-overview">
        <PortfolioOverview portfolio={portfolio} />
        <PortfolioLinkStatus portfolio={portfolio} />
      </section>
      <HoldingsOverview
        portfolio={portfolio}
        sortedHoldings={sortedHoldings}
        excludedHoldings={excludedHoldings}
      />
    </Grid>
  );
};
